import { useCallback, useState } from "react";

/**
 * Single-open cell-popover controller shared by the Coverage Summary + Detail
 * grids. Owns the open cell, the open-deferral hack (react-tiny-popover would
 * otherwise self-close on the originating click's outside-click listener), and
 * the closing cell whose wrapper stays mounted through its exit animation
 * (Phase B). Only the open (or closing) cell mounts a popover — the whole perf
 * win, since idle cells carry no document listeners.
 *
 * `K` is any per-cell key; pass a `serialize` so keys compare by value.
 */
export type CellPopoverController<K> = {
	/** The currently-open cell key, or `null`. */
	openKey: K | null;
	/** The cell whose popover is animating closed (kept mounted until it finishes), or `null`. */
	closingKey: K | null;
	isOpen: (key: K) => boolean;
	isClosing: (key: K) => boolean;
	/** Open the cell (deferred past the click) if closed, else close it. */
	toggle: (key: K) => void;
	/** Close whatever is open; the outgoing cell becomes the closing cell. */
	close: () => void;
	/** Drop the closing cell once its exit animation completes, so its wrapper can unmount. */
	clearClosing: (key: K) => void;
};

export const useCellPopover = <K>(serialize: (key: K) => string): CellPopoverController<K> => {
	const [openKey, setOpenKey] = useState<K | null>(null);
	const [closingKey, setClosingKey] = useState<K | null>(null);

	const isOpen = useCallback((key: K) => openKey !== null && serialize(openKey) === serialize(key), [openKey, serialize]);
	const isClosing = useCallback((key: K) => !isOpen(key) && closingKey !== null && serialize(closingKey) === serialize(key), [isOpen, closingKey, serialize]);

	const open = useCallback(
		(key: K) => {
			setOpenKey(key);
			setClosingKey((c) => (c !== null && serialize(c) === serialize(key) ? null : c));
		},
		[serialize],
	);

	const close = useCallback(() => {
		setOpenKey((prev) => {
			if (prev !== null) setClosingKey(prev);
			return null;
		});
	}, []);

	const toggle = useCallback(
		(key: K) => {
			if (openKey !== null && serialize(openKey) === serialize(key)) {
				close();
				return;
			}
			// Defer the open past the originating click so react-tiny-popover's
			// outside-click listener doesn't immediately close it again.
			setTimeout(() => open(key), 0);
		},
		[openKey, serialize, close, open],
	);

	const clearClosing = useCallback(
		(key: K) => {
			setClosingKey((c) => (c !== null && serialize(c) === serialize(key) ? null : c));
		},
		[serialize],
	);

	return { openKey, closingKey, isOpen, isClosing, toggle, close, clearClosing };
};
